<script setup lang="ts">
/**
 * Aura Incorporation — Form 45B (Consent to Act as Secretary, s.171(1AA)) — **VL 고정본**.
 *   고객 회신(2026-10-26) "Forms 45 and 45B still reflecting the old versions" — Incorporation 비서는 항상
 *   FLORENSIA LEOVANNY LIONG 이라, On Board 와 같은 "Form 45B - VL" 템플릿을 쓴다.
 *   비서 인적사항·서명(이미지)은 템플릿 원문 고정, 입력은 회사명 · Company No · 취임일 · Dated 만.
 *   화면 키는 Incorporation 기존 키(company_registration_no / appointment_effective_date / execution_date) 그대로 —
 *   normalize 가 템플릿 토큰(company_uen / effective_date / dated_on)으로 alias.
 *   부수(f45b_{id}/) 의 secretary_* 는 VL 로 채워 둔다 → 문서명("Form 45B — FLORENSIA …") 연동.
 */
import { inject, onMounted, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { DOC_SCOPE, useCddFill } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');

/** 템플릿에 고정된 VL 인적사항 (Form 45B - VL) — On Board OnBoardForm45bDoc 와 동일 */
const VL = {
  secretary_name: 'FLORENSIA LEOVANNY LIONG',
  secretary_residential_address: '76A REDHILL ROAD #07-04 SINGAPORE 151076',
  secretary_contact_address: '1 PHILLIP STREET #08-00 ROYAL ONE PHILLIP SINGAPORE 048692',
  secretary_identification_no: 'S7684980C',
  secretary_nationality: 'INDONESIA',
} as const;

function seedVl() {
  for (const [k, v] of Object.entries(VL)) {
    if (fill.values[scope + k] !== v) fill.setValue(scope + k, v);
  }
}
onMounted(seedVl);
// T-Link 자동채움 등으로 부수 비서 값이 바뀌거나 지워져도 다시 VL 로
watch(() => Object.keys(VL).map((k) => fill.values[scope + k]).join('|'), seedVl);
</script>

<template>
  <div class="allot-doc f45b">
    <div class="f45b-head">
      <div class="f45b-title">
        <div>THE COMPANIES ACT 1967</div>
        <div>SECTION 173C(b)</div>
        <div>CONSENT TO ACT AS</div>
        <div>SECRETARY</div>
      </div>
      <div class="f45b-form">Form<br />45B</div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name of Company</td><td>: <CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Company No</td><td>: <CddVarField field-key="company_registration_no" label="Company No" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p">
      1. &nbsp;I, the undermentioned person, hereby consent to act as a secretary of the abovenamed company with effect
      from <CddVarField field-key="appointment_effective_date" label="Effective Date" />.
    </p>
    <p class="doc-p">
      <sup>#</sup>2. &nbsp;I am a qualified person under section 171(1AA) of the Companies Act by virtue of my being &mdash;
    </p>
    <p class="doc-p ind">
      (i) a secretary of a company for at least 3 years of the 5 years immediately preceding the abovementioned date of my
      appointment as secretary of the abovenamed company.
    </p>
    <!-- 원문(VL)에서 해당 없는 자격은 취소선으로 지움 -->
    <p class="doc-p ind strike">*(ii) a qualified person under the Legal Profession Act 1966.</p>
    <p class="doc-p ind strike">*(iii) a public accountant.</p>
    <p class="doc-p ind strike">
      *(iiia) a member of the Institute of Singapore Chartered Accountants (formerly known as the Institute of Certified
      Public Accountants of Singapore).
    </p>
    <p class="doc-p ind strike">*(iv) a member of the Chartered Secretaries Institute of Singapore.</p>
    <p class="doc-p ind strike">*(v) a member of the Association of International Accountants (Singapore Branch).</p>
    <p class="doc-p ind strike">*(vi) a member of The Institute of Company Accountants, Singapore.</p>

    <!-- 비서 인적사항 — 템플릿 고정값 -->
    <table class="cdd-tbl vl-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name</td><td>: <b>{{ VL.secretary_name }}</b></td></tr>
        <tr><td class="cdd-lcell">Residential Address</td><td>: <b>{{ VL.secretary_residential_address }}</b></td></tr>
        <tr><td class="cdd-lcell">Contact Address</td><td>: <b>1 PHILLIP STREET #08-00 ROYAL ONE PHILLIP<br />&nbsp; SINGAPORE 048692</b></td></tr>
        <tr>
          <td class="cdd-lcell">*NRIC/ <span class="strike">Passport No</span></td>
          <td>: <b>{{ VL.secretary_identification_no }}</b> &nbsp;&nbsp; Nationality : <b>{{ VL.secretary_nationality }}</b></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Signature</td>
          <td>: ............................. <span class="doc-note">(signature fixed in the form)</span></td>
        </tr>
      </tbody>
    </table>
    <p class="doc-note vl-note">Secretary particulars and signature are fixed in this form (Form 45B – VL) and are not edited here.</p>

    <div class="doc-meta" style="margin-top:10px">Dated: <CddVarField field-key="execution_date" label="Date" /></div>

    <div class="f45b-foot">
      <p><sup>#</sup>&nbsp; to be completed by secretaries of public companies only or by secretaries of private companies appointed under section 171(1AB) of the Act.</p>
      <p>* &nbsp;Delete where inapplicable.</p>
    </div>
  </div>
</template>

<style scoped>
.f45b-head { display: flex; gap: 14px; justify-content: center; margin-bottom: 16px; }
.f45b-title { border: 1px solid #888; padding: 10px 28px; text-align: center; font-weight: 700; font-size: 12px; line-height: 1.7; }
.f45b-form { border: 1px solid #888; padding: 10px 22px; text-align: center; font-weight: 700; font-size: 16px; line-height: 1.3; }
.ind { padding-left: 22px; }
.strike { text-decoration: line-through; }
.vl-tbl { margin-top: 10px; }
.vl-note { margin-top: 4px; color: #6b7280; font-size: 11px; }
.f45b-foot { margin-top: 16px; font-size: 11px; }
.f45b-foot p { margin: 0 0 2px; }
</style>
